/**
 * 铁路询价供应商报价前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest } from '@/api/types/common'
import {
  type InitiateConsultationRequest,
  type QuotationBaseModel,
  type QuotationChargingItemVO
} from './quotation-base'

export interface QuotationRailModel extends QuotationBaseModel {
  paymentTerm?: string | null
  loadingCountryId?: string | number | null
  loadingCityId?: string | number | null
  loadingRailwayStationId?: string | number | null
  destinationCountryId?: string | number | null
  destinationCityId?: string | number | null
  destinationStationId?: string | number | null
  routeType?: string | null
  needCustomsClearance?: boolean | null
  needInsurance?: boolean | null
  serviceType?: string | null
  hsCode?: string | null
  onePriceSupported?: boolean | null
  quotationDeadline?: string | null
  specialRequirements?: string | null
  chargingItemList?: QuotationChargingItemVO[] | null
  trainDepartureTime?: string | null
  trainArrivalTime?: string | null
  borderCrossTime?: string | null
  overweightRemarks?: string | null
  demurrageFreeDays?: number | null
  weekdays?: number[] | null
}

export const getQuotationRailById = (id: string) => {
  return request.post<QuotationRailModel>('/price/quotation/rail/get', { id })
}

export const queryQuotationRailByInquiry = (params: {
  inquiryIds?: Array<string | number>
  inquiryNo?: string | null
}) => {
  return request.post<QuotationRailModel[]>('/price/quotation/rail/query', params)
}

export const initiateConsultationRail = (params: InitiateConsultationRequest) => {
  return request.post<boolean>('/price/quotation/rail/initiateConsultation', params)
}

export const batchDeleteQuotationRail = (params: BatchDeleteRequest) => {
  return request.post<boolean>('/price/quotation/rail/batch-delete', params)
}

import type { LinkAccess, BusinessFileVO } from './quotation-fcl'

/** 铁路询价货物（InquiryRailCargoVO） */
export interface InquiryRailCargoVO {
  id?: string
  inquiryId?: string
  /** 箱型（集装箱） */
  containerType?: string
  /** 箱量（集装箱） */
  containerNum?: number
  /** 整车类型（整车）：1-敞车 2-棚车 3-平车 4-冷藏车 5-矿石车 6-水泥车 */
  vehicleType?: number
  /** 件数 */
  quantity?: number
  /** 毛重 */
  weight?: number
  length?: number
  width?: number
  height?: number
  /** 包装方式 */
  packageId?: number
  /** 温度范围-起始 */
  temperatureStart?: number
  /** 温度范围-结束 */
  temperatureEnd?: number
  /** 通风门 */
  ventilationDoor?: boolean
  /** 通风量 */
  ventilationQuantity?: number
  /** 湿度 */
  humidity?: number
  /** 是否预冷 */
  preCooling?: boolean
  remarks?: string
  [key: string]: unknown
}

/** 铁路询价基础信息（InquiryRailListVO + 父类关键字段） */
export interface InquiryRailListVO {
  id?: string
  inquiryNo?: string
  status?: number
  /** 付款方式 PREPAID/COLLECT */
  paymentTerm?: string
  /** 服务条款（贸易条款） */
  serviceTerm?: string
  /** 运输条款 */
  transportTerms?: string
  /** 询价性质（palletNature 1-询价 2-实单） */
  palletNature?: number
  /** 起运国 */
  loadingCountryId?: string
  /** 起运城市 */
  loadingCityId?: string
  /** 起运铁路站 */
  loadingRailwayStationId?: string
  /** 目的国 */
  destinationCountryId?: string
  /** 目的城市 */
  destinationCityId?: string
  /** 目的站 */
  destinationStationId?: string
  /** 铁路公司 */
  carrierId?: string
  /** 线路分类 */
  routeType?: string
  /** 中转站ID列表 */
  transferPortIds?: string[]
  /** 服务类型 CONTAINER/TRUCK/BULK */
  serviceType?: string
  /** 货好时间类型 */
  cargoReadyTimeType?: string
  cargoReadyTimeRange?: number
  cargoReadyTimeDate?: string
  /** 期望开航时间（etd） */
  etd?: string
  /** 货物类型 */
  cargoType?: number
  /** UN 编号 */
  unNo?: number
  /** 类别 */
  hazardClass?: number
  /** 品名 */
  cargoDescription?: string
  hsCode?: string
  cargoValue?: number
  cargoValueCurrency?: string
  onePriceSupported?: boolean
  quotationDeadline?: string
  specialRequirements?: string
  /** 是否需要清关 */
  needCustomsClearance?: boolean
  /** 是否需要保险 */
  needInsurance?: boolean
  cargoList?: InquiryRailCargoVO[]
  [key: string]: unknown
}

/** 铁路报价费用条目（QuotationRailChargingItemVO） */
export interface QuotationRailChargingItemVO {
  id?: string
  quotationId?: string
  chargeItemId?: string | number
  /** 费用主类型：1=港前 2=铁路 3=尾程 */
  feeMainType?: number
  /** 费用类型：0/1 铁路，10/11/12 港前，20/21/22 尾程 */
  feeType?: number
  /** 付费条款：1-预付 2-到付 */
  paymentType?: number
  /** 单位ID */
  measureId?: string
  /** 计费单位类型：1=按箱 2=非按箱 */
  billingUnitType?: number
  /** 箱型价格 JSON：{ "20GP": 150, "40HQ": 260 } */
  eqPrice?: Record<string, number> | string
  /** 箱型卖价 JSON：{ "20GP": 150, "40HQ": 260 }（询价人模式取此槽） */
  eqPriceSell?: Record<string, number> | string
  quantity?: number
  currency?: string
  /** 单价 */
  unitRate?: number | string
  /** 单价卖价（询价人模式取此槽） */
  unitRateSell?: number | string
  amount?: number
  remarks?: string
  /** 整车基础费用：车种（字符串输入） */
  truckType?: string
  /** 整车基础费用：标重（字符串输入） */
  stdWeight?: string
  /** 整车基础费用：基价1 */
  basePrice1?: number | string
  /** 整车基础费用：基价2 */
  basePrice2?: number | string
  /** 散货基础费用/附加费：最低收费 */
  minCharge?: number | string
  /** 散货基础费用/附加费：最高收费 */
  maxCharge?: number | string
  [key: string]: unknown
}

/** 铁路报价方案（QuotationRailListVO + 父类字段） */
export interface QuotationRailListVO {
  id?: string
  quotationNo?: string
  status?: number
  inquiryId?: string
  inquiryNo?: string
  supplierId?: string
  supplierName?: string
  contactPhone?: string
  contactEmail?: string
  quoterId?: string
  consultationStatus?: number
  remarks?: string
  supplierRemarks?: string
  onePrice?: boolean
  onePriceSupported?: boolean
  validFrom?: string
  validTo?: string
  cargoType?: number
  paymentTerm?: string
  loadingCountryId?: string
  loadingCityId?: string
  destinationCountryId?: string
  destinationCityId?: string
  serviceType?: string
  hsCode?: string
  quotationDeadline?: string
  specialRequirements?: string
  /** 铁路公司（父类 carrierId） */
  carrierId?: string
  /** 线路分类 */
  routeType?: string
  /** 起运站 */
  loadingRailwayStationId?: string
  /** 目的站 */
  destinationStationId?: string
  /** 直达/中转 1-直达 2-中转 3-不指定 */
  directTransit?: number
  /** 中转站ID列表（新增字段） */
  transferPortIds?: string[]
  /** 班期（新增字段） */
  weekdays?: number[]
  /** 发车时间 */
  trainDepartureTime?: string
  /** 出境时间 */
  borderCrossTime?: string
  /** 到站时间 */
  trainArrivalTime?: string
  /** 限重（文本输入） */
  overweightRemarks?: string
  /** 免堆存天数 */
  demurrageFreeDays?: number
  chargingItemList?: QuotationRailChargingItemVO[]
  cargoList?: InquiryRailCargoVO[]
  files?: BusinessFileVO[]
  // 港前字段
  preTransportMode?: number
  prePickupPortId?: string
  preCarrierId?: string
  preVessel?: string
  preVoyage?: string
  preBargeEtd?: string
  preBargeEta?: string
  preTransportationDays?: number
  prePickupRailwayStationId?: string
  preRailCompanyId?: string
  preRailLineType?: string
  preRailTrainNo?: string
  preRailDepartureTime?: string
  preLoadingPortId?: string
  preStuffingTerminalId?: string
  preLoadingRailwayStationId?: string
  preLoadingAirportId?: string
  preAirportTerminal?: string
  preRemarks?: string
  // 尾程字段
  lastTransportMode?: number
  lastDischargingPortId?: string
  lastDeliveryTerminalId?: string
  lastDischargingStationId?: string
  lastDischargingAirportId?: string
  lastAirportTerminal?: string
  lastDestinationPortId?: string
  lastVessel?: string
  lastVoyage?: string
  lastBargeEtd?: string
  lastBargeEta?: string
  lastTransportationDays?: number
  lastDestinationStationId?: string
  lastRailCompanyId?: string
  lastRailLineType?: string
  lastRailTrainNo?: string
  lastRailDepartureTime?: string
  lastRemarks?: string
  [key: string]: unknown
}

/** 铁路匿名报价详情（含询价信息） */
export interface QuotationRailAnonymousDetailVO {
  /** 报价单列表 */
  quotations?: QuotationRailListVO[]
  /** 询价单信息 */
  inquiry?: InquiryRailListVO
}

/**
 * 通过 token 查询邮件授权范围内的铁路报价方案列表（含询价信息）
 * 后端：QuotationRailController#queryByInquiryAnonymous
 */
export const queryByInquiryRailAnonymous = (access: LinkAccess) =>
  request.post<QuotationRailAnonymousDetailVO>('/price/quotation/rail/anonymous/queryByInquiry', { token: access.token })

export const createAnonymousQuotationRailScheme = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<number>('/price/quotation/rail/anonymous/createScheme', { quotation, token: access.token })

export const updateAnonymousQuotationRail = (quotation: Record<string, unknown>, access: LinkAccess) =>
  request.post<boolean>('/price/quotation/rail/anonymous/update', { quotation, token: access.token })

export const batchSubmitAnonymousQuotationRail = (quotations: Record<string, unknown>[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/rail/anonymous/batchSubmit', { quotations, token: access.token })

export const batchDeleteAnonymousQuotationRail = (ids: (string | number)[], access: LinkAccess) =>
  request.post<boolean>('/price/quotation/rail/anonymous/batchDelete', { ids, token: access.token })

export type { LinkAccess, BusinessFileVO }
